import React, { useState, useEffect, useRef, useCallback } from 'react';
import { userService } from "../../../_services";
import { Navigate } from 'react-router-dom';
import moment from 'moment';
import TableWidget from '../TableWidget';
import FilterTagCom from '../FilterTag.jsx';
import DateRangePicker from "@/components/ui/date-range-picker";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Loader2, Download, Search } from "lucide-react";
import { Badge } from "@/components/ui/badge";

/**
 * Pharmacies Analytics Page
 * Modernized with shadcn components - Pharmacy data visualization
 */
function Pharmacies() {
    // Initialize dates
    const getInitialStartDate = () => {
        const stored = localStorage.getItem('User_Start_Date');
        if (stored) return stored;
        const date = new Date();
        date.setDate(date.getDate() - 30);
        return date.toISOString().split('T')[0];
    };

    const getInitialEndDate = () => {
        const stored = localStorage.getItem('User_End_Date');
        return stored || new Date().toISOString().split('T')[0];
    };

    // State
    const [startDate, setStartDate] = useState(getInitialStartDate());
    const [endDate, setEndDate] = useState(getInitialEndDate());
    const [graphData, setGraphData] = useState([]);
    const [filterTagList, setFilterTagList] = useState('');
    const [selectedTab, setSelectedTab] = useState('Total');
    const [searchInput, setSearchInput] = useState('');
    const [searchDisplay, setSearchDisplay] = useState([]);
    const [showSearchResults, setShowSearchResults] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [legendValues, setLegendValues] = useState({});
    const [widgetHasData, setWidgetHasData] = useState({});

    const roleName = 'patient rx discount';
    const whId = '61';
    const roleType = 'Wholesaler';

    useEffect(() => {
        localStorage.setItem('Filter_Last_Update_Time', new Date());
        localStorage.setItem('pagename', 'pharmaciesPage');
        // Initialize claim type
        window.localStorage.setItem('Updated_HD_Type', 'totalpaid');
        changeData();
    }, []);

    useEffect(() => {
        changeData();
    }, [startDate, endDate]);

    const handleChangeStartEnd = (type, value) => {
        if (type === 'start') {
            setStartDate(value);
            localStorage.setItem('User_Start_Date', value);
        } else if (type === 'end') {
            setEndDate(value);
            localStorage.setItem('User_End_Date', value);
        }
    };

    const handleDateRangeChange = (event, picker) => {
        const newStartDate = picker.startDate.format('YYYY-MM-DD');
        const newEndDate = picker.endDate.format('YYYY-MM-DD');
        setStartDate(newStartDate);
        setEndDate(newEndDate);
        localStorage.setItem('User_Start_Date', newStartDate);
        localStorage.setItem('User_End_Date', newEndDate);
    };

    // Mirrors Create_Advance_FIlter_Json_Without_Refresh('PBM','HD','RadioSelect') from reference branch.
    // Sets PCN:HD in Advance_Filter so the filter tag appears when a non-Total tab is active.
    const applyPCNHDFilter = () => {
        try {
            let filter = null;
            try { filter = JSON.parse(localStorage.getItem('Advance_Filter')); } catch (_) {}

            if (filter && typeof filter === 'object') {
                // Clear existing PBM values then set HD (mirrors the splice(0,...) + push logic)
                filter['PBM'] = ['HD'];
                localStorage.setItem('Advance_Filter', JSON.stringify(filter));
            } else {
                localStorage.setItem('Advance_Filter', JSON.stringify({ PBM: ['HD'] }));
            }
            const now = new Date().toString();
            localStorage.setItem('Filter_Last_Update_Time', now);
            localStorage.setItem('Updated_AdFilter_Status', localStorage.getItem('Advance_Filter'));
        } catch (_) {}
    };

    // Remove PCN:HD filter when returning to Total tab
    const removePCNHDFilter = () => {
        try {
            let filter = null;
            try { filter = JSON.parse(localStorage.getItem('Advance_Filter')); } catch (_) {}
            if (filter && typeof filter === 'object' && filter['PBM']) {
                delete filter['PBM'];
                const updated = Object.keys(filter).length === 0 ? null : JSON.stringify(filter);
                localStorage.setItem('Advance_Filter', updated);
                const now = new Date().toString();
                localStorage.setItem('Filter_Last_Update_Time', now);
                localStorage.setItem('Updated_AdFilter_Status', updated || '');
            }
        } catch (_) {}
    };

    const handleTabChange = (value) => {
        setSelectedTab(value);
        let claimType = 'totalpaid';
        if (value === 'Reversed') claimType = 'reversed';
        else if (value === 'Net') claimType = 'netpaid';
        else if (value === 'Paid') claimType = 'compensable';

        // Save current value as Prev before overwriting (mirrors reference branch logic)
        localStorage.setItem('Prev_HD_Type', localStorage.getItem('Updated_HD_Type'));
        window.localStorage.setItem('Updated_HD_Type', claimType);

        if (claimType !== 'totalpaid') {
            applyPCNHDFilter();
        }
        // PCN:HD filter is only removed when the user explicitly presses X or Reset

        changeData();
    };

    const handleChangeFilter = (e) => {
        // Sync tab back to Total when Updated_HD_Type was reset to totalpaid by FilterTag
        // (e.g. after Reset button or after removing the PCN:HD tag via X).
        // Use setSelectedTab directly instead of DOM click to avoid double changeData() calls.
        const hdType = localStorage.getItem('Updated_HD_Type');
        if (!hdType || hdType === 'totalpaid') {
            setSelectedTab('Total');
        }

        // Single data refresh
        changeData();
    };

    const handleSearchKeyUp = async (e) => {
        const value = e.target.value.trim();
        setSearchInput(e.target.value);
        if (value.length < 2) {
            setSearchDisplay([]);
            setShowSearchResults(false);
            return;
        }
        try {
            setSearchLoading(true);
            const response = await fetch('/api/Filter/SearchOnTypeText', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ text: value, roletype: roleType, rolename: roleName, whname: whId })
            });
            const data = await response.json();
            if (data && data.length > 0) {
                setSearchDisplay(data);
                setShowSearchResults(true);
            } else {
                setSearchDisplay([]);
                setShowSearchResults(false);
            }
        } catch (err) {
            setSearchDisplay([]);
            setShowSearchResults(false);
        } finally {
            setSearchLoading(false);
        }
    };

    const handleSearchItemClick = (item) => {
        let existingFilter = {};
        try { existingFilter = JSON.parse(localStorage.getItem('Advance_Filter')) || {}; } catch (_) {}
        const key = item.ctype || item.cname;
        if (!existingFilter[key]) existingFilter[key] = [];
        if (!existingFilter[key].some(v => v.toLowerCase() === item.cvalue.toLowerCase())) {
            existingFilter[key].push(item.cvalue);
        }
        localStorage.setItem('Advance_Filter', JSON.stringify(existingFilter));
        localStorage.setItem('Filter_Last_Update_Time', new Date().toString());
        localStorage.setItem('Updated_AdFilter_Status', JSON.stringify(existingFilter));
        setSearchInput('');
        setSearchDisplay([]);
        setShowSearchResults(false);
        changeData();
    };

    // Called by each widget when legend data is loaded (number before title)
    const exportHandlersRef = useRef({});

    const handleLegendUpdate = useCallback((widgetId, value) => {
        setLegendValues(prev => ({ ...prev, [widgetId]: value }));
    }, []);

    const makeExportReady = useCallback((widgetId) => (fn) => {
        exportHandlersRef.current[widgetId] = fn;
    }, []);

    const makeDataReady = useCallback((widgetId) => (hasData) => {
        setWidgetHasData(prev => ({ ...prev, [widgetId]: hasData }));
    }, []);

    const changeData = () => {
        setLoading(true);
        setLegendValues({});
        setWidgetHasData({});
        const APIBaseURL = '/api/ws';
        const startDateFormatted = moment(startDate).format("YYYYMMDD");
        const endDateFormatted = moment(endDate).format("YYYYMMDD");

        // Parse advanced filter
        let AdFilterJson = '';
        try {
            AdFilterJson = JSON.parse(localStorage.getItem('Advance_Filter'));
            if (AdFilterJson != null && AdFilterJson != '' && AdFilterJson != undefined && AdFilterJson != "null") {
                setFilterTagList(JSON.stringify(AdFilterJson));
            } else {
                setFilterTagList('');
            }
        } catch (error) {
            setFilterTagList('');
        }

        const Pharmacy_Transactions_Widget_PostData_RequestJson = JSON.stringify({
            wname: "61",
            start_date: startDateFormatted,
            end_date: endDateFormatted,
            rcount: 30,
            chart_type: "list",
            ispatientsaving: "true",
            pharmacynumber: "all",
            filtertime: localStorage.getItem('Filter_Last_Update_Time'),
            rxsClaimType: localStorage.getItem('Updated_HD_Type'),
            wholesaler_id: "61"
        });

        const Pharmacy_PatientSavings_Widget_PostData_RequestJson = JSON.stringify({
            wname: "61",
            start_date: startDateFormatted,
            end_date: endDateFormatted,
            rcount: 30,
            chart_type: "list",
            ispatientsaving: "true",
            pharmacynumber: "all",
            filtertime: localStorage.getItem('Filter_Last_Update_Time'),
            rxsClaimType: localStorage.getItem('Updated_HD_Type'),
            wholesaler_id: "61"
        });

        setGraphData([
            {
                url: APIBaseURL + "/Pharmacy_Detail_Wise_Transaction",
                type: "Table",
                title: "Transactions by Pharmacy",
                Subtitle: "Number of transactions at each pharmacy",
                ColorCode: "#ffba10",
                XlabelString: "",
                YlabelString: "",
                DataType: "PharmacyName",
                id: "WPharmacyTransaction",
                RequestJson: Pharmacy_Transactions_Widget_PostData_RequestJson,
                RequestJson1: JSON.stringify(AdFilterJson)
            },
            {
                url: APIBaseURL + "/Sum_PaitentSavings_By_Pharmacy",
                type: "Table",
                title: "Patient Savings by Pharmacy",
                Subtitle: "Patient savings in $ at each pharmacy",
                ColorCode: "#ffba10",
                XlabelString: "",
                YlabelString: "",
                DataType: "PharmacyName",
                id: "WPharmacyPatientSavings",
                RequestJson: Pharmacy_PatientSavings_Widget_PostData_RequestJson,
                RequestJson1: JSON.stringify(AdFilterJson)
            }
        ]);

        setLoading(false);
    };

    // Auth check - Admin only
    const userRole = window.atob(localStorage.getItem('UserRole') || '');
    if (userService.Check_Null_Empty(localStorage.getItem("ajs_user_id"))) {
        return <Navigate to="/signup" replace />;
    }
    if (userRole !== "Admin") {
        return <Navigate to="/page-not-found" replace />;
    }

    return (
        <div className="w-full space-y-6">
            {/* Heading with Beta Badge */}
            <div className="flex items-center gap-2">
                <h1 className="text-3xl font-bold tracking-tight text-foreground">Pharmacies</h1>
                <Badge variant="brand" className="uppercase">
                    Beta
                </Badge>
            </div>

            {/* Controls Row */}
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
                {/* Date Range Picker */}
                <DateRangePicker
                    startDate={moment(startDate)}
                    endDate={moment(endDate)}
                    onApply={handleDateRangeChange}
                    displayMode="unified"
                    containerClass="w-full sm:w-auto"
                />

                {/* Search */}
                    <div className="relative w-full lg:flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-primary/50 z-10" />
                        <Input
                            className="pl-10 pr-9"
                            placeholder="Search..."
                            autoComplete="off"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyUp={handleSearchKeyUp}
                            onFocus={() => searchDisplay.length > 0 && setShowSearchResults(true)}
                            onBlur={() => setTimeout(() => setShowSearchResults(false), 150)}
                        />
                        {searchLoading && (
                            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-primary/40 z-10" />
                        )}
                        {showSearchResults && searchDisplay.length > 0 && (
                            <div className="absolute top-full left-0 right-0 mt-1 bg-popover border border-border rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
                                {searchDisplay.slice(0, 5).map((item, index) => (
                                    <div
                                        key={index}
                                        className="px-3 py-2 hover:bg-muted cursor-pointer border-b border-muted last:border-b-0 flex items-center justify-between gap-2"
                                        onMouseDown={() => handleSearchItemClick(item)}
                                    >
                                        <span className="text-sm font-semibold text-primary leading-tight">
                                            {item.cvalue}
                                        </span>
                                        <span className="text-xs font-semibold text-primary leading-tight">
                                            {item.cname === 'PBM' ? 'PCN' : item.cname}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                {/* Tabs */}
                <Tabs value={selectedTab} onValueChange={handleTabChange} className="w-full lg:w-auto">
                        <TabsList className="grid grid-cols-4 w-full bg-background">
                            <TabsTrigger id="totalpaid1" value="Total">Total</TabsTrigger>
                            <TabsTrigger value="Reversed">Reversed</TabsTrigger>
                            <TabsTrigger value="Net">Net</TabsTrigger>
                            <TabsTrigger value="Paid">Paid</TabsTrigger>
                        </TabsList>
                    </Tabs>
            </div>

            {/* Filter Tags */}
            <FilterTagCom
                Rolename="Admin"
                wh_Id={whId}
                Roletype="Admin"
                df={moment(startDate).format("YYYYMMDD")}
                dt={moment(endDate).format("YYYYMMDD")}
                handlechangea={handleChangeFilter}
                FilterTags={filterTagList}
                Loading={true}
            />

            {/* Main Content */}
            {loading ? (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-accent" />
                </div>
            ) : (
                <>
                    {/* Widgets Grid */}
                    <div className="grid grid-cols-12 gap-6 mt-6">
                        {graphData.map((widget, index) => (
                            <Card
                                key={`widget-${widget.id}-${index}`}
                                className="col-span-12 2xl:col-span-6"
                            >
                                <CardHeader className="border-b px-6 py-4">
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="flex-1 min-w-0">
                                            <CardTitle className="text-lg font-semibold text-primary flex items-center gap-2 flex-wrap">
                                        {!loading && legendValues[widget.id] && (
                                                    <span className="text-accent font-bold">
                                                        {legendValues[widget.id]}
                                                    </span>
                                        )}
                                        {widget.title}
                                    </CardTitle>
                                    {widget.Subtitle && (
                                                <p className="text-sm text-accent mt-1">
                                            {widget.Subtitle}
                                        </p>
                                    )}
                                        </div>
                                        <TooltipProvider>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <Button
                                                        variant="outline"
                                                        size="icon"
                                                        disabled={widgetHasData[widget.id] !== true}
                                                        className={`h-8 w-8 shrink-0 text-primary border-primary/20 hover:bg-muted ${widgetHasData[widget.id] !== true ? 'opacity-50 pointer-events-none' : ''}`}
                                                        onClick={() => exportHandlersRef.current[widget.id]?.()}
                                                    >
                                                        <Download className="h-4 w-4" />
                                                    </Button>
                                                </TooltipTrigger>
                                                <TooltipContent>Download Report</TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    </div>
                                </CardHeader>
                                <CardContent className="p-6 overflow-hidden">
                                    <TableWidget
                                        data={widget}
                                        _Index={index}
                                        handlechangea={handleChangeFilter}
                                        onLegendUpdate={handleLegendUpdate}
                                        onExportReady={makeExportReady(widget.id)}
                                        onDataReady={makeDataReady(widget.id)}
                                    />
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

export default Pharmacies;
